// Copyright (c) Docugami, Inc. All rights reserved.

import { emptyGray, homeGray, primaryThemeSettings, themeBlue } from "components/shell/presentation/AppStyles"
import styled from "styled-components"
import React from "react"
import { dgFontFamily, userFontFamily, UserInfo } from "components/common/Fonts"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { DgProgressBar } from "components/common/DgProgressBar"

const NotificationItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 5px;
    padding-top: 5px;
    box-sizing: border-box;
`

const StatusBarContainer = styled.div`
    width: calc(100% - 35px);
    margin-left: 25px;
`

const ActionBar = styled.div`
    display: flex;
    flex-direction: row;
    align-self: flex-end;
    justify-content: flex-end;
    span {
        color: ${primaryThemeSettings.palette.themePrimary};
        :hover {
            cursor: pointer;
            text-decoration: underline;
        }
        font-family: ${dgFontFamily};
        font-weight: 500;
        font-size: 14px;
    }
`;

const MessageContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    :hover {
        cursor: pointer;
    }
`;

const AlertMessage = styled.span`
    font-family: ${userFontFamily};
    font-weight: 400;
    font-size: 16px;
    color: ${homeGray};
`;

const BulletContainer = styled.div<{isRead: boolean}>`
    visibility: ${props => props.isRead ? 'hidden' : "visible"};
    span {
        font-size: 40px;
        color: ${themeBlue};
    }
`;

const TimestampContainer = styled(UserInfo)`
    align-self: flex-end;
    font-size: 14px;
    color: ${emptyGray};
`;

const SecondarySpan = styled.span`
    padding-right: 10px;
    border-right: 1px solid ${emptyGray};
    margin-right: 10px;
`;

export interface IProgressVisual {
    progress?: number;
}

export interface LabelledAction {
    label: string;
    action: () => void;
}

export interface NotificationItemProps {
    primaryAction: LabelledAction;
    secondaryAction?: LabelledAction;
    message: string;
    progressVisual?: IProgressVisual;
    isRead: boolean;
    createdAt: Date;
}

/**
 * Visual representation of a notification in the UI
 */
export const NotificationItem = (props: NotificationItemProps): JSX.Element => {

    function calculateTimestamp(): string {
        dayjs.extend(relativeTime);
        const createdAt = dayjs(props.createdAt);
        const createdAtDate = createdAt.date();
        const nowDate = dayjs().date();
        if (createdAtDate === nowDate) {
            // Show the timestamp
            return createdAt.format('h[:]mm a');
        }
        else {
            // Show the relative difference
            return dayjs().to(createdAt, true);
        }
    }

    function getProgress() {
        if (!props.progressVisual) {
            return <></>;
        }

        return (
            <StatusBarContainer>
                <DgProgressBar percentComplete={props.progressVisual.progress} height={10} />
            </StatusBarContainer>
        );
    }

    function populateActionBar() {
        return <ActionBar>
            {!!props.secondaryAction &&
                <SecondarySpan onClick={() => props.secondaryAction!.action()}>
                    {props.secondaryAction.label}
                </SecondarySpan>}
            <span onClick={() => onClick()}>
                {props.primaryAction.label}
            </span>
        </ActionBar>
    }

    function onClick() {
        props.primaryAction.action();
    }

    return (
        <NotificationItemContainer>
            <TimestampContainer>{calculateTimestamp()}</TimestampContainer>
            <MessageContainer onClick={() => onClick()}>
                <BulletContainer isRead={props.isRead}>
                    <span>{"\u2022"}</span>
                </BulletContainer>
                <AlertMessage>{props.message}</AlertMessage>
            </MessageContainer>
            { getProgress()}
            { populateActionBar()}
        </NotificationItemContainer>
    )
}
